import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, FormControl, Grid, IconButton,
  MenuItem, Select, Stack, TextField, Typography
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from '../../utils/axios';
import { decryptSalaryConfig, decryptSalaryGrade } from '../../utils/salaryCrypto';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../store/hooks';

const defaultForm = {
  _id: '',
  employee: '',
  salaryGrade: '',
  effectiveFrom: '',
  salaryType: 'Monthly',
  ctcAmount: '',
  earnings: [{ head: '', value: '', percent: '' }] as Array<{ head: string; value: string; percent: string }>,
  deductions: [{ head: '', value: '', percent: '' }] as Array<{ head: string; value: string; percent: string }>,
};

const fieldSx: any = {
  fontFamily: "'Ubuntu', sans-serif !important",
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#e9ecf1',
    borderRadius: '5px',
    fontSize: 13,
    fontFamily: "'Ubuntu', sans-serif",
    color: '#000',
    minHeight: 35,
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' },
  },
  '& .MuiOutlinedInput-input': { fontSize: 13, fontFamily: "'Ubuntu', sans-serif", color: '#000', padding: '6px 10px !important' },
  '& .MuiAutocomplete-inputRoot': { padding: '3px 6px !important', minHeight: 35, fontFamily: "'Ubuntu', sans-serif" },
  '& .MuiAutocomplete-input': { padding: '2px 4px !important', fontFamily: "'Ubuntu', sans-serif" },
};

const whiteFieldSx: any = {
  fontFamily: "'Ubuntu', sans-serif !important",
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff',
    borderRadius: '10px',
    fontSize: 13,
    fontFamily: "'Ubuntu', sans-serif",
    color: '#000',
    minHeight: 35,
    '& fieldset': { borderColor: '#9da7b7', borderWidth: '0.5px' },
    '&:hover fieldset': { borderColor: '#488dff' },
    '&.Mui-focused fieldset': { borderColor: '#488dff', borderWidth: '1px' },
  },
  '& .MuiOutlinedInput-input': { fontSize: 13, fontFamily: "'Ubuntu', sans-serif", color: '#000', padding: '6px 10px !important' },
  '& .MuiAutocomplete-inputRoot': { padding: '3px 8px !important', minHeight: 35, fontFamily: "'Ubuntu', sans-serif" },
  '& .MuiAutocomplete-input': { padding: '2px 4px !important', fontFamily: "'Ubuntu', sans-serif" },
};

const selectSx = {
  ...fieldSx,
  '& .MuiSelect-select': { fontSize: 13, fontFamily: "'Ubuntu', sans-serif", padding: '6px 28px 6px 6px', display: 'flex', alignItems: 'center', color: '#000' },
};

const whiteSelectSx = {
  ...whiteFieldSx,
  '& .MuiSelect-select': { fontSize: 13, fontFamily: "'Ubuntu', sans-serif", padding: '6px 28px 6px 6px', display: 'flex', alignItems: 'center', color: '#000' },
};

const FieldLabel: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography component="h5" sx={{ fontSize: 13, fontWeight: 400, color: '#9da7b7', mb: '4px', pl: '2px', lineHeight: 1.5, fontFamily: "'Ubuntu', sans-serif" }}>{children}</Typography>
);

const EmployeeSalaryConfigForm: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { selectedAccount } = useAppSelector(state => state.auth);

  const [form, setForm] = useState(defaultForm);
  const [employees, setEmployees] = useState<any[]>([]);
  const [salaryGrades, setSalaryGrades] = useState<any[]>([]);
  const [salaryHeads, setSalaryHeads] = useState<any[]>([]);
  const salaryHeadsRef = useRef<any[]>([]);
  const [setupAllowanceIds, setSetupAllowanceIds] = useState<string[]>([]);
  const [setupDeductionIds, setSetupDeductionIds] = useState<string[]>([]);
  const setupAllowanceIdsRef = useRef<string[]>([]);
  const setupDeductionIdsRef = useRef<string[]>([]);
  const [tdsRateOptions, setTdsRateOptions] = useState<any[]>([]);
  const [selectedTdsId, setSelectedTdsId] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const effectiveFromRef = useRef<HTMLInputElement | null>(null);

  const getDecimalValue = (v: any) => {
    if (v === null || v === undefined || v === '') return 0;
    if (typeof v === 'object' && v.$numberDecimal !== undefined) return Number(v.$numberDecimal || 0);
    return Number(v || 0);
  };

  const loadData = useCallback(async () => {
    try {
      const [empRes, gradeRes, headRes, tdsRes, setupRes] = await Promise.all([
        axios.get(`/api/v1/employee/account/${selectedAccount?._id}/emp-list?limit=100`),
        axios.get('/api/v1/payroll/salaryGrade?limit=100'),
        axios.get('/api/v1/payroll/salaryHead?limit=500'),
        axios.get('/api/v1/gst/tax-rates'),
        axios.get('/api/v1/payroll/companySalaryHead?limit=10').catch(() => ({ data: { data: [] } })),
      ]);
      setEmployees(empRes.data?.employees || []);
      // Grade values arrive encrypted — decrypt so the grade→CTC auto-fill works.
      setSalaryGrades((gradeRes.data?.data || []).map((g: any) => decryptSalaryGrade(g)));
      const heads = headRes.data?.data || [];
      setSalaryHeads(heads);
      salaryHeadsRef.current = heads;
      const setupDoc = (setupRes.data?.data || [])[0];
      const allowIds = setupDoc ? [
        ...(setupDoc.companyDefaultAllowanceHeads || []),
        ...(setupDoc.companyCustomAllowanceHeads || []),
      ].map((x: any) => String(x?._id || x)) : [];
      const dedIds = setupDoc ? [
        ...(setupDoc.employeePFContributionHeads || []),
        ...(setupDoc.employeeESIContributionHeads || []),
        ...(setupDoc.customeDeductionsHeads || []),
      ].map((x: any) => String(x?._id || x)) : [];
      setSetupAllowanceIds(allowIds);
      setSetupDeductionIds(dedIds);
      setupAllowanceIdsRef.current = allowIds;
      setupDeductionIdsRef.current = dedIds;
      const all: any[] = tdsRes.data?.taxRates || tdsRes.data?.data || [];
      setTdsRateOptions(all.filter((r: any) => r.taxType === 'tds').map((r: any) => ({
        value: r._id,
        label: `${r.taxRate}% - ${r.taxSection || ''} ${r.taxName || ''}`.trim(),
        rate: r.taxRate,
      })));
    } catch {
      toast.error('Failed to load data');
    }
  }, [selectedAccount?._id]);

  const loadRecord = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/v1/payroll/employeeSalaryConfig/${id}`);
      // Amounts come back encrypted from the API — decrypt for editing.
      const row = decryptSalaryConfig(res.data?.data);
      if (row) {
        setForm({
          _id: row._id,
          employee: row.employee?._id || row.employee || '',
          salaryGrade: row.salaryGrade?._id || row.salaryGrade || '',
          effectiveFrom: row.effectiveFrom ? String(row.effectiveFrom).slice(0, 10) : '',
          salaryType: row.salaryType || row.salryType || 'Monthly',
          ctcAmount: String(getDecimalValue(row.ctcAmount) || ''),
          earnings: row.companyAllowanceHeads?.length
            ? row.companyAllowanceHeads.map((i: any) => ({ head: i.head || '', value: String(getDecimalValue(i.value) || ''), percent: String(i.percent || '') }))
            : [{ head: '', value: '', percent: '' }],
          deductions: row.deductionsHeads?.length
            ? row.deductionsHeads.map((i: any) => ({ head: i.head || '', value: String(getDecimalValue(i.value) || ''), percent: String(i.percent || '') }))
            : [{ head: '', value: '', percent: '' }],
        });
        if (row.tdsRateId) setSelectedTdsId(row.tdsRateId);
      }
    } catch {
      toast.error('Failed to load record');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (id) loadRecord();
  }, [id, loadRecord]);

  const calcAmountForHead = (head: any, annualCtc: number, basicMonthly: number) => {
    if (!head) return 0;
    const monthly = annualCtc / 12;
    if (head.calculationType === 'Fixed Amount') return Number(head.defaultAmount || 0);
    if (head.calculationType === 'Percentage of Basic') return Number(((head.percentage || 0) / 100 * basicMonthly).toFixed(2));
    if (head.calculationType === 'Percentage of CTC' || head.calculationType === 'Percentage of Gross')
      return Number(((head.percentage || 0) / 100 * monthly).toFixed(2));
    return 0;
  };

  const recalcRows = (rows: typeof form.earnings, annualCtc: number) => {
    const monthly = annualCtc / 12;
    const basicHead = salaryHeads.find(h => /basic/i.test(h.headName));
    const basicAmount = basicHead ? calcAmountForHead(basicHead, annualCtc, monthly * 0.5) : monthly * 0.5;
    return rows.map(r => {
      const head = salaryHeads.find(h => h.headName === r.head);
      if (!head) return r;
      const val = calcAmountForHead(head, annualCtc, basicAmount);
      return { head: r.head, value: val > 0 ? String(val) : r.value, percent: r.percent };
    });
  };

  const handleCtcChange = (value: string) => {
    const ctc = Number(value || 0);
    setForm(prev => ({
      ...prev,
      ctcAmount: value,
      earnings: ctc ? recalcRows(prev.earnings, ctc) : prev.earnings,
      deductions: ctc ? recalcRows(prev.deductions, ctc) : prev.deductions,
    }));
  };

  const handleGradeChange = (gradeId: string) => {
    const grade = salaryGrades.find(g => g._id === gradeId);
    const ctc = getDecimalValue(grade?.gradeValue);
    setForm(prev => ({
      ...prev,
      salaryGrade: gradeId,
      ctcAmount: ctc ? String(ctc) : prev.ctcAmount,
      earnings: ctc ? recalcRows(prev.earnings, ctc) : prev.earnings,
      deductions: ctc ? recalcRows(prev.deductions, ctc) : prev.deductions,
    }));
  };

  const handleHeadSelect = (section: 'earnings' | 'deductions', index: number, headName: string) => {
    const ctc = Number(form.ctcAmount || 0);
    const monthly = ctc / 12;
    const basicHead = salaryHeads.find(h => /basic/i.test(h.headName));
    const basicAmount = basicHead ? calcAmountForHead(basicHead, ctc, monthly * 0.5) : monthly * 0.5;
    const head = salaryHeads.find(h => h.headName === headName);
    let val = 0;
    let pct = '';
    if (head) {
      if (ctc) val = calcAmountForHead(head, ctc, basicAmount);
      else if (head.calculationType === 'Fixed Amount') val = Number(head.defaultAmount || 0);
      if (head.percentage && Number(head.percentage) > 0) pct = String(head.percentage);
    }
    setForm(prev => ({
      ...prev,
      [section]: prev[section].map((item, idx) => idx === index ? { head: headName, value: val > 0 ? String(val) : '', percent: pct } : item),
    }));
  };

  const updateRow = (section: 'earnings' | 'deductions', index: number, field: 'head' | 'value' | 'percent', value: string) => {
    const ctc = Number(form.ctcAmount || 0);
    const monthly = ctc / 12;
    setForm(prev => {
      // Current BASIC monthly amount (use the edited value in the form if present, else master config).
      const basicRow = prev.earnings.find(r => /basic/i.test(r.head));
      const basicHead = salaryHeads.find(h => /basic/i.test(h.headName));
      const basicAmount = basicRow && Number(basicRow.value) > 0
        ? Number(basicRow.value)
        : (basicHead ? calcAmountForHead(basicHead, ctc, monthly * 0.5) : monthly * 0.5);

      return {
        ...prev,
        [section]: prev[section].map((item, idx) => {
          if (idx !== index) return item;
          const updated: any = { ...item, [field]: value };
          // The base the percentage applies to depends on the head's master calculation type.
          const head = salaryHeads.find(h => h.headName === item.head);
          const base = head?.calculationType === 'Percentage of Basic' ? basicAmount : monthly;
          if (field === 'percent' && base) {
            const pct = Number(value || 0);
            updated.value = pct > 0 ? String(Number((base * pct / 100).toFixed(2))) : '';
          } else if (field === 'value' && base) {
            const v = Number(value || 0);
            updated.percent = v > 0 ? String(Number((v / base * 100).toFixed(2))) : '';
          }
          return updated;
        }),
      };
    });
  };

  const addRow = (section: 'earnings' | 'deductions') =>
    setForm(prev => ({ ...prev, [section]: [...prev[section], { head: '', value: '', percent: '' }] }));

  const removeRow = (section: 'earnings' | 'deductions', index: number) =>
    setForm(prev => ({ ...prev, [section]: prev[section].filter((_, i) => i !== index) }));

  // A "Balance" allowance head auto-absorbs the remaining-to-allocate amount
  // (monthly gross − all other allowances) and recalculates whenever anything changes.
  useEffect(() => {
    const monthly = Number(form.ctcAmount || 0) / 12;
    if (!monthly) return;
    const balanceIdx = form.earnings.findIndex(r => {
      const h = salaryHeads.find(x => x.headName === r.head);
      return h?.calculationType === 'Balance';
    });
    if (balanceIdx === -1) return;
    const others = form.earnings.reduce(
      (sum, r, i) => i === balanceIdx ? sum : sum + (Number(r.value) || 0),
      0
    );
    const bal = Math.max(0, Number((monthly - others).toFixed(2)));
    const current = Number(form.earnings[balanceIdx].value || 0);
    if (Math.abs(bal - current) > 0.001) {
      setForm(prev => ({
        ...prev,
        earnings: prev.earnings.map((r, i) => i === balanceIdx ? { ...r, value: String(bal), percent: '' } : r),
      }));
    }
  }, [form.earnings, form.ctcAmount, salaryHeads]);

  const totalAmount = (items: Array<{ value: string }>) =>
    items.reduce((sum, item) => sum + (Number(item.value) || 0), 0);

  const buildItems = (items: Array<{ head: string; value: string; percent?: string }>) =>
    items.filter(i => i.head && i.value !== '' && Number(i.value) > 0)
      .map(i => ({ head: i.head, value: Number(i.value), percent: i.percent ? Number(i.percent) : 0 }));

  const handleSave = async () => {
    if (!form.employee || !form.effectiveFrom || !form.ctcAmount) {
      toast.error('Employee, effective date and annual CTC are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        employee: form.employee,
        salaryGrade: form.salaryGrade || undefined,
        effectiveFrom: form.effectiveFrom,
        salaryType: form.salaryType,
        salryType: form.salaryType,
        ctcAmount: Number(form.ctcAmount || 0),
        companyAllowanceHeads: buildItems(form.earnings),
        deductionsHeads: buildItems(form.deductions),
        tdsRateId: selectedTdsId || undefined,
      };
      if (form._id) {
        await axios.put(`/api/v1/payroll/employeeSalaryConfig/${form._id}`, payload);
        toast.success('Salary config updated');
      } else {
        await axios.post('/api/v1/payroll/employeeSalaryConfig', payload);
        toast.success('Salary config created');
      }
      navigate('/payroll-settings?tab=employeeSalaryConfig');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  const ctc = Number(form.ctcAmount || 0);
  const monthlyGross = ctc / 12;
  const allocated = totalAmount(form.earnings);
  const unallocated = monthlyGross - allocated;
  const allocPct = monthlyGross > 0 ? Math.min(100, (allocated / monthlyGross) * 100) : 0;
  const totalDed = totalAmount(form.deductions);
  // Net Take Home is the monthly gross minus deductions. Allowances (`allocated`)
  // are a breakup *within* the gross, not an addition to it — so an empty
  // breakup still nets the full gross, not ₹0.
  const netPay = monthlyGross - totalDed;
  const isOver = unallocated < 0;
  const isComplete = monthlyGross > 0 && Math.abs(unallocated) < 1;

  const existingConfiguredIds = useMemo(() => new Set<string>(), []);
  const employeeOptions = form._id ? employees : employees.filter(e => !existingConfiguredIds.has(String(e._id)));

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><Typography>Loading...</Typography></Box>;

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', bgcolor: '#f9fafb', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{
        flexShrink: 0, zIndex: 10, bgcolor: 'transparent',
        px: 1.5, py: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 500, color: '#000', fontFamily: "'Ubuntu', sans-serif" }}>
            {form._id ? 'Edit Salary Config' : 'Add Salary Config'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="outlined" onClick={() => navigate('/payroll-settings?tab=employeeSalaryConfig')} sx={{ textTransform: 'none', height: 36, fontSize: 14, fontFamily: "'Ubuntu', sans-serif" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', height: 36, minWidth: 100, fontSize: 14, fontFamily: "'Ubuntu', sans-serif" }}>
            {saving ? 'Saving...' : form._id ? 'Update' : 'Save'}
          </Button>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
      <Box>
        <Grid container spacing={2}>
          {/* Left — Form */}
          <Grid item xs={12} md={9}>
            <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2.5, p: 1.5 }}>
              <Grid container columnSpacing={2} rowSpacing={2}>

                {/* Employee */}
                <Grid item xs={8}>
                  <FieldLabel>Employee *</FieldLabel>
                  <Autocomplete
                    size="small"
                    options={employeeOptions}
                    value={employees.find(e => e._id === form.employee) || null}
                    onChange={async (_, opt: any) => {
                      if (!form._id && opt?._id) {
                        let heads = salaryHeadsRef.current;
                        let allowIds = setupAllowanceIdsRef.current;
                        let dedIds = setupDeductionIdsRef.current;

                        if (!heads.length) {
                          try {
                            const hr = await axios.get('/api/v1/payroll/salaryHead?limit=500');
                            heads = hr.data?.data || [];
                            setSalaryHeads(heads);
                            salaryHeadsRef.current = heads;
                          } catch { /* ignore */ }
                        }
                        try {
                          const sr = await axios.get('/api/v1/payroll/companySalaryHead?limit=10');
                          const doc = (sr.data?.data || [])[0];
                          if (doc) {
                            allowIds = [
                              ...(doc.companyDefaultAllowanceHeads || []),
                              ...(doc.companyCustomAllowanceHeads || []),
                            ].map((x: any) => String(x?._id || x));
                            dedIds = [
                              ...(doc.employeePFContributionHeads || []),
                              ...(doc.employeeESIContributionHeads || []),
                              ...(doc.customeDeductionsHeads || []),
                            ].map((x: any) => String(x?._id || x));
                            setSetupAllowanceIds(allowIds);
                            setSetupDeductionIds(dedIds);
                            setupAllowanceIdsRef.current = allowIds;
                            setupDeductionIdsRef.current = dedIds;
                          }
                        } catch { /* ignore */ }

                        const ctc = Number(form.ctcAmount || 0);
                        const monthly = ctc / 12;
                        const basicHead = heads.find(h => /^basic$/i.test(h.headName || ''));
                        const basicAmount = basicHead ? calcAmountForHead(basicHead, ctc, monthly * 0.5) : monthly * 0.5;
                        const headRow = (h: any) => {
                          const val = ctc ? calcAmountForHead(h, ctc, basicAmount)
                            : (h.calculationType === 'Fixed Amount' ? Number(h.defaultAmount || 0) : 0);
                          return {
                            head: h.headName,
                            value: val > 0 ? String(val) : '',
                            percent: h.percentage && Number(h.percentage) > 0 ? String(h.percentage) : '',
                          };
                        };
                        const earnings = allowIds
                          .map(id => heads.find(h => String(h._id) === id))
                          .filter((h: any) => h && h.isActive !== false && ['Earning', 'Reimbursement'].includes(h.componentType || 'Earning'))
                          .map(headRow);
                        const dedHeads: any[] = dedIds
                          .map(id => heads.find(h => String(h._id) === id))
                          .filter((h: any) => h && h.isActive !== false && ['Deduction', 'Employer Contribution'].includes(h.componentType || ''));
                        const hasEsi = dedHeads.some((h: any) => /^esi$/i.test(h.headName || ''));
                        if (!hasEsi) {
                          const esiMaster = heads.find(h => /^esi$/i.test(h.headName || ''));
                          if (esiMaster) dedHeads.push(esiMaster);
                        }
                        const deductions = dedHeads.map(headRow);
                        setForm(prev => ({
                          ...prev,
                          employee: opt._id,
                          earnings: earnings.length ? earnings : [{ head: '', value: '', percent: '' }],
                          deductions: deductions.length ? deductions : [{ head: '', value: '', percent: '' }],
                        }));
                      } else {
                        setForm(prev => ({ ...prev, employee: opt?._id || '' }));
                      }
                    }}
                    getOptionLabel={(opt: any) => `${opt?.name || 'Unnamed'} (${opt?.empId || '—'})`}
                    isOptionEqualToValue={(opt: any, val: any) => opt?._id === val?._id}
                    filterOptions={(opts, state) => {
                      const q = state.inputValue.trim().toLowerCase();
                      if (!q) return opts;
                      return opts.filter((e: any) =>
                        String(e.name || '').toLowerCase().includes(q) ||
                        String(e.empId || '').toLowerCase().includes(q) ||
                        String(e.personalEmail || e.businessEmail || '').toLowerCase().includes(q)
                      );
                    }}
                    renderOption={(props, opt: any) => (
                      <Box component="li" {...props} key={opt._id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 0.8 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{opt.name || 'Unnamed'}</Typography>
                        <Typography sx={{ fontSize: 11, color: '#6b7280' }}>{opt.empId || '—'}{opt.designation ? ` · ${opt.designation}` : ''}</Typography>
                      </Box>
                    )}
                    renderInput={(params) => <TextField {...params} placeholder="Type name, ID, email..." size="small" sx={whiteFieldSx} />}
                  />
                </Grid>

                {/* Salary Grade */}
                <Grid item xs={4}>
                  <FieldLabel>Salary Grade</FieldLabel>
                  <FormControl fullWidth size="small" sx={whiteSelectSx}>
                    <Select
                      value={form.salaryGrade}
                      onChange={(e) => handleGradeChange(e.target.value)}
                      displayEmpty
                      renderValue={(val) => val ? (salaryGrades.find(g => g._id === val)?.gradeName || val) : <em style={{ color: '#9ca3af', fontStyle: 'normal' }}>Custom (No Grade)</em>}
                    >
                      <MenuItem value=""><em style={{ color: '#9ca3af' }}>Custom (No Grade)</em></MenuItem>
                      {salaryGrades.map(g => <MenuItem key={g._id} value={g._id}>{g.gradeName}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Salary Type */}
                <Grid item xs={4}>
                  <FieldLabel>Salary Type</FieldLabel>
                  <FormControl fullWidth size="small" sx={whiteSelectSx}>
                    <Select value={form.salaryType} onChange={(e) => setForm(prev => ({ ...prev, salaryType: e.target.value }))}>
                      <MenuItem value="Monthly">Monthly</MenuItem>
                      <MenuItem value="Daily">Daily</MenuItem>
                      <MenuItem value="Hourly">Hourly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Effective From */}
                <Grid item xs={4}>
                  <FieldLabel>Effective From *</FieldLabel>
                  <TextField
                    fullWidth size="small" type="date"
                    inputRef={effectiveFromRef}
                    value={form.effectiveFrom}
                    onChange={(e) => setForm(prev => ({ ...prev, effectiveFrom: e.target.value }))}
                    sx={{
                      ...whiteFieldSx,
                      '& input[type="date"]::-webkit-calendar-picker-indicator': { display: 'none' },
                      '& .MuiOutlinedInput-root': { ...whiteFieldSx['& .MuiOutlinedInput-root'], height: 36 },
                    }}
                    InputProps={{
                      endAdornment: (
                        <IconButton size="small" onClick={() => { const i = effectiveFromRef.current as any; if (i?.showPicker) i.showPicker(); else effectiveFromRef.current?.focus(); }} sx={{ color: '#64748b', p: 0.5 }}>
                          <CalendarMonthIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )
                    }}
                  />
                </Grid>

                {/* Annual CTC */}
                <Grid item xs={4}>
                  <FieldLabel>Annual CTC *</FieldLabel>
                  <TextField
                    fullWidth size="small" type="number" sx={whiteFieldSx}
                    value={form.ctcAmount}
                    onChange={(e) => handleCtcChange(e.target.value)}
                    InputProps={{ startAdornment: <span style={{ marginRight: 4, color: '#6b7280', fontSize: 14 }}>₹</span> }}
                  />
                </Grid>

                {/* Allowances + Deductions side by side */}
                <Grid item xs={12}>
                  <Grid container spacing={2} alignItems="stretch">
                    {/* Allowances */}
                    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2.5, p: 1.5, width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#000', fontFamily: "'Ubuntu', sans-serif" }}>Allowances</Typography>
                          <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => addRow('earnings')} sx={{ textTransform: 'none', height: 28, fontSize: 13, fontFamily: "'Ubuntu', sans-serif" }}>Add</Button>
                        </Box>
                        <Grid container columnSpacing={1} rowSpacing={1}>
                          <Grid item xs={5}><Typography sx={{ fontSize: 13, fontWeight: 400, color: '#9da7b7', fontFamily: "'Ubuntu', sans-serif" }}>Head</Typography></Grid>
                          <Grid item xs={3}><Typography sx={{ fontSize: 13, fontWeight: 400, color: '#9da7b7', fontFamily: "'Ubuntu', sans-serif" }}>Allocated</Typography></Grid>
                          <Grid item xs={3}><Typography sx={{ fontSize: 13, fontWeight: 400, color: '#9da7b7', fontFamily: "'Ubuntu', sans-serif" }}>Amount</Typography></Grid>
                          <Grid item xs={1} />
                          {form.earnings.map((item, index) => (
                            <React.Fragment key={`e-${index}`}>
                              <Grid item xs={5}>
                                <Autocomplete
                                  size="small"
                                  options={salaryHeads.filter(h => ['Earning', 'Reimbursement'].includes(h.componentType || 'Earning'))}
                                  value={salaryHeads.find(h => h.headName === item.head) || null}
                                  onChange={(_, opt: any) => handleHeadSelect('earnings', index, opt?.headName || '')}
                                  getOptionLabel={(opt: any) => opt.headName || ''}
                                  renderOption={(props, opt: any) => (
                                    <Box component="li" {...props} key={opt._id}>
                                      <Box>
                                        <Typography sx={{ fontSize: 13 }}>{opt.headName}</Typography>
                                        {opt.headType?.name && <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{opt.headType.name}</Typography>}
                                      </Box>
                                    </Box>
                                  )}
                                  renderInput={(params) => <TextField {...params} placeholder="Head..." size="small" sx={fieldSx} />}
                                />
                              </Grid>
                              {(() => {
                                const head = salaryHeads.find(h => h.headName === item.head);
                                const isPercentBased = !!head?.calculationType?.includes('Percentage');
                                const isBalance = head?.calculationType === 'Balance';
                                const masterPct = head?.percentage ? String(head.percentage) : '';
                                return (
                                  <>
                                    <Grid item xs={3}>
                                      <TextField fullWidth size="small" sx={fieldSx}
                                        type="number" placeholder={isPercentBased ? (masterPct || '0') : (isBalance ? 'Auto' : '—')}
                                        value={isPercentBased ? (item.percent || '') : ''}
                                        disabled={!isPercentBased}
                                        onChange={(e) => updateRow('earnings', index, 'percent', e.target.value)}
                                        InputProps={{ endAdornment: <span style={{ fontSize: 11, color: '#9ca3af' }}>%</span> }}
                                      />
                                    </Grid>
                                    <Grid item xs={3}>
                                      <TextField fullWidth size="small" sx={fieldSx} type="number" value={item.value}
                                        disabled={isPercentBased || isBalance}
                                        onChange={(e) => updateRow('earnings', index, 'value', e.target.value)}
                                        InputProps={{ startAdornment: <span style={{ marginRight: 2, color: '#6b7280', fontSize: 12 }}>₹</span> }}
                                      />
                                    </Grid>
                                  </>
                                );
                              })()}
                              <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton size="small" onClick={() => removeRow('earnings', index)}>
                                  <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Grid>
                            </React.Fragment>
                          ))}
                          {form.earnings.length === 0 && (
                            <Grid item xs={12}>
                              <Typography sx={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', py: 1.5, fontFamily: "'Ubuntu', sans-serif" }}>
                                No allowance added
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Grid>

                    {/* Deductions */}
                    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2.5, p: 1.5, width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#000', fontFamily: "'Ubuntu', sans-serif" }}>Deductions</Typography>
                          <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => addRow('deductions')} sx={{ textTransform: 'none', height: 28, fontSize: 13, fontFamily: "'Ubuntu', sans-serif" }}>Add</Button>
                        </Box>
                        <Grid container columnSpacing={1} rowSpacing={1}>
                          <Grid item xs={5}><Typography sx={{ fontSize: 13, fontWeight: 400, color: '#9da7b7', fontFamily: "'Ubuntu', sans-serif" }}>Head</Typography></Grid>
                          <Grid item xs={3}><Typography sx={{ fontSize: 13, fontWeight: 400, color: '#9da7b7', fontFamily: "'Ubuntu', sans-serif" }}>Allocated</Typography></Grid>
                          <Grid item xs={3}><Typography sx={{ fontSize: 13, fontWeight: 400, color: '#9da7b7', fontFamily: "'Ubuntu', sans-serif" }}>Amount</Typography></Grid>
                          <Grid item xs={1} />
                          {form.deductions.map((item, index) => (
                            <React.Fragment key={`d-${index}`}>
                              <Grid item xs={5}>
                                <Autocomplete
                                  size="small"
                                  options={salaryHeads.filter(h => ['Deduction', 'Employer Contribution'].includes(h.componentType || ''))}
                                  value={salaryHeads.find(h => h.headName === item.head) || null}
                                  onChange={(_, opt: any) => handleHeadSelect('deductions', index, opt?.headName || '')}
                                  getOptionLabel={(opt: any) => opt.headName || ''}
                                  renderOption={(props, opt: any) => (
                                    <Box component="li" {...props} key={opt._id}>
                                      <Box>
                                        <Typography sx={{ fontSize: 13 }}>{opt.headName}</Typography>
                                        {opt.headType?.name && <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{opt.headType.name}</Typography>}
                                      </Box>
                                    </Box>
                                  )}
                                  renderInput={(params) => <TextField {...params} placeholder="Head..." size="small" sx={fieldSx} />}
                                />
                              </Grid>
                              {(() => {
                                const head = salaryHeads.find(h => h.headName === item.head);
                                const isPercentBased = !!head?.calculationType?.includes('Percentage');
                                const masterPct = head?.percentage ? String(head.percentage) : '';
                                return (
                                  <>
                                    <Grid item xs={3}>
                                      <TextField fullWidth size="small" sx={fieldSx}
                                        type="number" placeholder={isPercentBased ? (masterPct || '0') : '—'}
                                        value={isPercentBased ? (item.percent || '') : ''}
                                        disabled={!isPercentBased}
                                        onChange={(e) => updateRow('deductions', index, 'percent', e.target.value)}
                                        InputProps={{ endAdornment: <span style={{ fontSize: 11, color: '#9ca3af' }}>%</span> }}
                                      />
                                    </Grid>
                                    <Grid item xs={3}>
                                      <TextField fullWidth size="small" sx={fieldSx} type="number" value={item.value}
                                        disabled={isPercentBased}
                                        onChange={(e) => updateRow('deductions', index, 'value', e.target.value)}
                                        InputProps={{ startAdornment: <span style={{ marginRight: 2, color: '#6b7280', fontSize: 12 }}>₹</span> }}
                                      />
                                    </Grid>
                                  </>
                                );
                              })()}
                              <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton size="small" onClick={() => removeRow('deductions', index)}>
                                  <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Grid>
                            </React.Fragment>
                          ))}
                          {form.deductions.length === 0 && (
                            <Grid item xs={12}>
                              <Typography sx={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', py: 1.5, fontFamily: "'Ubuntu', sans-serif" }}>
                                No deduction added
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                        {form.deductions.some(d => /^tds$/i.test(d.head)) && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: '#fef3c7', border: '1px solid #fde68a', borderRadius: 1 }}>
                            <FieldLabel>TDS Rate</FieldLabel>
                            <FormControl fullWidth size="small" sx={selectSx}>
                              <Select
                                value={selectedTdsId}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setSelectedTdsId(v);
                                  const opt = tdsRateOptions.find(o => o.value === v);
                                  if (opt) {
                                    const monthly = Number(form.ctcAmount || 0) / 12;
                                    const amt = monthly ? Number((monthly * Number(opt.rate) / 100).toFixed(2)) : 0;
                                    setForm(prev => ({
                                      ...prev,
                                      deductions: prev.deductions.map(d => /^tds$/i.test(d.head)
                                        ? { ...d, percent: String(opt.rate), value: amt > 0 ? String(amt) : d.value }
                                        : d)
                                    }));
                                  }
                                }}
                                displayEmpty
                              >
                                <MenuItem value=""><em>Select TDS Rate / Section</em></MenuItem>
                                {tdsRateOptions.map((opt: any) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                              </Select>
                            </FormControl>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Right — Summary */}
          <Grid item xs={12} md={3}>
            <Box sx={{ position: 'sticky', top: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {monthlyGross > 0 && (
                <Box sx={{
                  bgcolor: isOver ? '#fef2f2' : isComplete ? '#f0fdf4' : '#fffbeb',
                  border: `1px solid ${isOver ? '#fecaca' : isComplete ? '#bbf7d0' : '#fde68a'}`,
                  borderRadius: 2, p: 2
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.8 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: isOver ? '#991b1b' : isComplete ? '#15803d' : '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {isOver ? 'Over-Allocated' : isComplete ? 'Fully Allocated' : 'Allocation Pending'}
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: isOver ? '#991b1b' : isComplete ? '#15803d' : '#92400e' }}>
                      {allocPct.toFixed(1)}%
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, color: isOver ? '#dc2626' : isComplete ? '#15803d' : '#d97706', mb: 0.4 }}>
                    ₹{fmt(Math.abs(unallocated))}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: isOver ? '#991b1b' : isComplete ? '#15803d' : '#92400e' }}>
                    {isOver ? 'Exceeds monthly gross. Reduce allowances.' : isComplete ? 'All salary distributed.' : 'Remaining to allocate'}
                  </Typography>
                  <Box sx={{ mt: 1, height: 6, bgcolor: '#fff', borderRadius: 3, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                    <Box sx={{ height: '100%', width: `${Math.min(100, allocPct)}%`, bgcolor: isOver ? '#dc2626' : isComplete ? '#15803d' : '#d97706', transition: 'width 0.3s ease' }} />
                  </Box>
                </Box>
              )}

              <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>Salary Summary</Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  {ctc === 0 ? (
                    <Typography sx={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', py: 2 }}>Enter Annual CTC to see summary</Typography>
                  ) : (
                    <>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>Annual CTC</Typography>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>₹{fmt(ctc)}</Typography>
                      </Box>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>Monthly Gross</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>₹{fmt(monthlyGross)}</Typography>
                      </Box>
                      <Box sx={{ height: '1px', bgcolor: '#e2e8f0', my: 1.5 }} />
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography sx={{ fontSize: 12, color: '#475569' }}>Total Allowances</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>₹{fmt(allocated)}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontSize: 12, color: '#475569' }}>Total Deductions</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#dc2626' }}>−₹{fmt(totalDed)}</Typography>
                      </Stack>
                      <Box sx={{ p: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 1.5 }}>
                        <Typography sx={{ fontSize: 10, color: '#15803d', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>Net Take Home</Typography>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#15803d' }}>₹{fmt(netPay)}</Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      </Box>
    </Box>
  );
};

export default EmployeeSalaryConfigForm;
